// app/settings/pipeline-config/components/IndustryTemplates.tsx
import { useState, useRef, useEffect, useCallback } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

interface WizardStage {
  id: number | string;
  name: string;
  color: string;
  required: boolean;
}

interface WizardField {
  id: number | string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

export interface IndustryTemplate {
  id: string;
  name: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  stages: number;
  fields: number;
  pipeline: string;
  active: boolean;
  isCustom?: boolean;
  stageList: WizardStage[];
  fieldList: WizardField[];
}

const twToHex: Record<string, string> = {
  "bg-green-500": "#22c55e",
  "bg-blue-500": "#3b82f6",
  "bg-indigo-500": "#6366f1",
  "bg-amber-500": "#f59e0b",
  "bg-orange-500": "#f97316",
  "bg-red-500": "#ef4444",
  "bg-purple-500": "#a855f7",
  "bg-teal-500": "#14b8a6",
  "bg-pink-500": "#ec4899",
  "bg-cyan-500": "#06b6d4",
  "bg-gray-400": "#9ca3af",
  "bg-green-600": "#16a34a",
};

const resolveColor = (c: string) => (c && twToHex[c] ? twToHex[c] : c || "#6366f1");

const builtInTemplates: IndustryTemplate[] = [
  {
    id: "real-estate", name: "Real Estate", icon: "ri-building-4-line", iconBg: "bg-indigo-100", iconColor: "text-indigo-600",
    stages: 7, fields: 8, pipeline: "New Lead → Site Visit → Proposal → Negotiation → Booking → Documentation → Closed Won", active: false,
    stageList: [
      { id: 1, name: "New Lead", color: "#22c55e", required: true },
      { id: 2, name: "Site Visit", color: "#3b82f6", required: true },
      { id: 3, name: "Proposal", color: "#6366f1", required: true },
      { id: 4, name: "Negotiation", color: "#f59e0b", required: false },
      { id: 5, name: "Booking", color: "#a855f7", required: false },
      { id: 6, name: "Documentation", color: "#14b8a6", required: false },
      { id: 7, name: "Closed Won", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Property Type", type: "Dropdown", required: true, options: ["2BHK", "3BHK", "Villa", "Plot", "Commercial"] },
      { id: 2, label: "Budget Range", type: "Dropdown", required: true, options: ["Below 30L", "30-50L", "50L-1Cr", "Above 1Cr"] },
      { id: 3, label: "Preferred Location", type: "Text Input", required: false },
      { id: 4, label: "Possession Timeline", type: "Dropdown", required: false, options: ["Ready to move", "1-2 years", "2-3 years", "3+ years"] },
      { id: 5, label: "Loan Required", type: "Toggle", required: false },
      { id: 6, label: "Site Visit Date", type: "Date", required: false },
      { id: 7, label: "Broker / Channel Partner", type: "Text Input", required: false },
      { id: 8, label: "Competitor Properties Seen", type: "Multi-select", required: false, options: ["Project A", "Project B", "Project C"] },
    ],
  },
  {
    id: "manufacturing", name: "Manufacturing", icon: "ri-building-2-line", iconBg: "bg-amber-100", iconColor: "text-amber-600",
    stages: 6, fields: 6, pipeline: "Inquiry → Sample → Quote → PO → Production → Delivery", active: false,
    stageList: [
      { id: 1, name: "Inquiry", color: "#22c55e", required: true },
      { id: 2, name: "Sample", color: "#3b82f6", required: false },
      { id: 3, name: "Quote", color: "#6366f1", required: true },
      { id: 4, name: "PO", color: "#f59e0b", required: false },
      { id: 5, name: "Production", color: "#a855f7", required: false },
      { id: 6, name: "Delivery", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Product Category", type: "Dropdown", required: true, options: ["Raw Material", "Finished Goods", "Components", "Packaging"] },
      { id: 2, label: "Order Quantity", type: "Number", required: true },
      { id: 3, label: "Delivery Timeline", type: "Dropdown", required: false, options: ["Immediate", "15 days", "30 days", "60+ days"] },
      { id: 4, label: "Payment Terms", type: "Dropdown", required: false, options: ["100% Advance", "50-50", "Net 30", "Letter of Credit"] },
      { id: 5, label: "Sample Required", type: "Toggle", required: false },
      { id: 6, label: "Target Price", type: "Number", required: false },
    ],
  },
  {
    id: "education", name: "Education", icon: "ri-graduation-cap-line", iconBg: "bg-green-100", iconColor: "text-green-600",
    stages: 5, fields: 5, pipeline: "Inquiry → Counseling → Application → Enrolled → Alumni", active: false,
    stageList: [
      { id: 1, name: "Inquiry", color: "#22c55e", required: true },
      { id: 2, name: "Counseling", color: "#3b82f6", required: false },
      { id: 3, name: "Application", color: "#6366f1", required: false },
      { id: 4, name: "Enrolled", color: "#a855f7", required: false },
      { id: 5, name: "Alumni", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Course Interested", type: "Dropdown", required: true, options: ["B.Tech", "MBA", "MCA", "BBA", "Diploma"] },
      { id: 2, label: "Batch Year", type: "Dropdown", required: false, options: ["2026", "2027", "2028"] },
      { id: 3, label: "Scholarship Required", type: "Toggle", required: false },
      { id: 4, label: "Previous Score", type: "Number", required: false },
      { id: 5, label: "Parent Contact", type: "Phone", required: false },
    ],
  },
  {
    id: "healthcare", name: "Healthcare", icon: "ri-heart-line", iconBg: "bg-red-100", iconColor: "text-red-600",
    stages: 6, fields: 7, pipeline: "Inquiry → Consultation → Diagnosis → Treatment → Follow-up → Discharged", active: false,
    stageList: [
      { id: 1, name: "Inquiry", color: "#22c55e", required: true },
      { id: 2, name: "Consultation", color: "#3b82f6", required: true },
      { id: 3, name: "Diagnosis", color: "#6366f1", required: false },
      { id: 4, name: "Treatment", color: "#f59e0b", required: false },
      { id: 5, name: "Follow-up", color: "#a855f7", required: false },
      { id: 6, name: "Discharged", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Treatment Type", type: "Dropdown", required: true, options: ["OPD", "Surgery", "Dental", "Cardiology", "Orthopedic"] },
      { id: 2, label: "Insurance Provider", type: "Text Input", required: false },
      { id: 3, label: "Doctor Assigned", type: "Dropdown", required: false, options: ["Dr. Sharma", "Dr. Mehta", "Dr. Patel", "Dr. Rao"] },
      { id: 4, label: "Appointment Date", type: "Date", required: false },
      { id: 5, label: "Medical History", type: "Text Input", required: false },
      { id: 6, label: "Allergies", type: "Text Input", required: false },
      { id: 7, label: "Emergency Contact", type: "Phone", required: false },
    ],
  },
  {
    id: "saas", name: "SaaS / IT", icon: "ri-code-line", iconBg: "bg-purple-100", iconColor: "text-purple-600",
    stages: 6, fields: 5, pipeline: "Lead → Demo Scheduled → Demo Done → Trial Active → Contract Sent → Closed Won", active: false,
    stageList: [
      { id: 1, name: "Lead", color: "#22c55e", required: true },
      { id: 2, name: "Demo Scheduled", color: "#3b82f6", required: true },
      { id: 3, name: "Demo Done", color: "#6366f1", required: false },
      { id: 4, name: "Trial Active", color: "#f59e0b", required: false },
      { id: 5, name: "Contract Sent", color: "#a855f7", required: false },
      { id: 6, name: "Closed Won", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Company Size", type: "Dropdown", required: true, options: ["1-10", "11-50", "51-200", "201-500", "500+"] },
      { id: 2, label: "Current CRM/Tool", type: "Text Input", required: false },
      { id: 3, label: "Monthly Budget (₹)", type: "Number", required: false },
      { id: 4, label: "Trial Required", type: "Toggle", required: false },
      { id: 5, label: "Decision Timeline", type: "Dropdown", required: false, options: ["Immediate", "1 Month", "1 Quarter", "Exploring"] },
    ],
  },
  {
    id: "retail", name: "Retail & Distribution", icon: "ri-shopping-bag-3-line", iconBg: "bg-pink-100", iconColor: "text-pink-600",
    stages: 5, fields: 4, pipeline: "Inquiry → Quote → Sample → Order → Repeat Customer", active: false,
    stageList: [
      { id: 1, name: "Inquiry", color: "#22c55e", required: true },
      { id: 2, name: "Quote", color: "#3b82f6", required: false },
      { id: 3, name: "Sample", color: "#6366f1", required: false },
      { id: 4, name: "Order", color: "#a855f7", required: false },
      { id: 5, name: "Repeat Customer", color: "#16a34a", required: false },
    ],
    fieldList: [
      { id: 1, label: "Product Category", type: "Dropdown", required: true, options: ["Apparel", "Electronics", "Groceries", "Home & Kitchen"] },
      { id: 2, label: "Order Value", type: "Number", required: false },
      { id: 3, label: "Payment Mode", type: "Dropdown", required: false, options: ["UPI", "Net Banking", "Credit Card", "Cheque"] },
      { id: 4, label: "GST Number", type: "Text Input", required: false },
    ],
  },
];

const iconOptions = [
  { icon: "ri-building-4-line", bg: "bg-indigo-100", color: "text-indigo-600" },
  { icon: "ri-building-2-line", bg: "bg-amber-100", color: "text-amber-600" },
  { icon: "ri-graduation-cap-line", bg: "bg-green-100", color: "text-green-600" },
  { icon: "ri-heart-line", bg: "bg-red-100", color: "text-red-600" },
  { icon: "ri-code-line", bg: "bg-purple-100", color: "text-purple-600" },
  { icon: "ri-shopping-bag-3-line", bg: "bg-pink-100", color: "text-pink-600" },
  { icon: "ri-store-2-line", bg: "bg-teal-100", color: "text-teal-600" },
  { icon: "ri-tools-line", bg: "bg-orange-100", color: "text-orange-600" },
  { icon: "ri-plane-line", bg: "bg-cyan-100", color: "text-cyan-600" },
  { icon: "ri-bank-line", bg: "bg-emerald-100", color: "text-emerald-600" },
  { icon: "ri-truck-line", bg: "bg-sky-100", color: "text-sky-600" },
  { icon: "ri-restaurant-2-line", bg: "bg-rose-100", color: "text-rose-600" },
];

const stageColors = [
  { name: "Green", hex: "#22c55e" },
  { name: "Blue", hex: "#3b82f6" },
  { name: "Indigo", hex: "#6366f1" },
  { name: "Amber", hex: "#f59e0b" },
  { name: "Orange", hex: "#f97316" },
  { name: "Red", hex: "#ef4444" },
  { name: "Purple", hex: "#a855f7" },
  { name: "Teal", hex: "#14b8a6" },
  { name: "Pink", hex: "#ec4899" },
  { name: "Cyan", hex: "#06b6d4" },
];

const fieldTypes = ["Text Input", "Number", "Dropdown", "Multi-select", "Date", "Toggle", "Phone", "Email", "Textarea", "Currency"];

const DEFAULT_WIZARD_STAGES: WizardStage[] = [
  { id: 1, name: "New Lead", color: "#22c55e", required: true },
  { id: 2, name: "Contacted", color: "#3b82f6", required: true },
  { id: 3, name: "Qualified", color: "#6366f1", required: true },
];

const DEFAULT_WIZARD_FIELDS: WizardField[] = [
  { id: 1, label: "Industry Type", type: "Dropdown", required: true, options: ["Option A", "Option B"] },
  { id: 2, label: "Budget", type: "Number", required: false },
];

type Props = {
  onSwitchApplied?: () => void;
};

export default function IndustryTemplates({ onSwitchApplied }: Props) {
  const { showToast } = useToast();

  const [customTemplates, setCustomTemplates] = useState<IndustryTemplate[]>([]);
  const [activeSelection, setActiveSelection] = useState<{ type: string; key: string }>(() => {
    const saved = localStorage.getItem("active_industry_key");
    return { type: "custom", key: saved || "real-estate" };
  });
  const [loading, setLoading] = useState(true);
  const [showCards, setShowCards] = useState(true);

  const [switchTarget, setSwitchTarget] = useState<IndustryTemplate | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardName, setWizardName] = useState("");
  const [wizardIcon, setWizardIcon] = useState(iconOptions[0]);
  const [wizardStages, setWizardStages] = useState<WizardStage[]>(DEFAULT_WIZARD_STAGES);
  const [wizardFields, setWizardFields] = useState<WizardField[]>(DEFAULT_WIZARD_FIELDS);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingIsActive, setEditingIsActive] = useState<boolean>(false);
  const [isSavingWizard, setIsSavingWizard] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // ── Fetch custom templates + active selection ─────────────────
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi<any>("/industry-templates/get-all", undefined, []);
      const list = Array.isArray(res) ? res : (res?.data || []);
      const rows = list.map((t: any) => ({
        id: String(t.id),
        name: t.name,
        icon: t.icon || "ri-building-4-line",
        iconBg: t.iconBg || "bg-indigo-100",
        iconColor: t.iconColor || "text-indigo-600",
        stages: t.stages,
        fields: t.fields,
        pipeline: t.pipeline,
        active: Boolean(t.active),
        isCustom: t.isCustom ?? true,
        stageList: (t.stageList || []).map((s: any, idx: number) => ({
          id: s.id || idx + 1,
          name: s.name,
          color: resolveColor(s.color),
          required: s.required ?? false,
        })),
        fieldList: (t.fieldList || []).map((f: any, idx: number) => ({
          id: f.id || idx + 1,
          label: f.label,
          type: f.type,
          required: f.required ?? false,
          options: f.options || [],
        })),
        inventoryFields: t.inventoryFields || [],
      }));

      // Deduplicate built-ins if they are already in the DB rows
      const cleanBuiltIns = builtInTemplates
        .filter(b => !rows.some((r: any) => r.name.toLowerCase() === b.name.toLowerCase() || r.id === b.id))
        .map(b => ({ ...b, active: false }));

      const allMerged = [...cleanBuiltIns, ...rows];
      setCustomTemplates(allMerged);

      // Determine active template from backend response
      const activeRow = rows.find((r: any) => r.active);
      if (activeRow) {
        setActiveSelection({ type: "custom", key: String(activeRow.id) });
        localStorage.setItem("active_industry_key", String(activeRow.id));
      } else if (res?.active?.key) {
        setActiveSelection({ type: res.active.type || "custom", key: String(res.active.key) });
        localStorage.setItem("active_industry_key", String(res.active.key));
      }
    } catch {
      showToast("Failed to load industry templates", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allTemplates: IndustryTemplate[] = customTemplates.map((t) => {
    const activeKey = String(activeSelection.key || "real-estate").toLowerCase().trim();
    const isMatch =
      String(t.id).toLowerCase() === activeKey ||
      t.name.toLowerCase().replace(/\s+/g, "-") === activeKey ||
      t.name.toLowerCase() === activeKey;
    return {
      ...t,
      active: isMatch,
    };
  });

  // ── Switch (apply) a template ──────────────────────────────────
  const handleSwitch = (template: IndustryTemplate) => {
    if (template.active) return;
    setOpenMenuId(null);
    setSwitchTarget(template);
  };

  const confirmSwitch = async () => {
    if (!switchTarget) return;
    setIsSwitching(true);
    try {
      await fetchApi("/industry-templates/switch", {
        method: "PATCH",
        body: JSON.stringify({
          type: "custom",
          key: switchTarget.id,
          stages: switchTarget.stageList.map((s) => ({ name: s.name, color: s.color, required: s.required })),
          fields: switchTarget.fieldList.map((f) => ({ label: f.label, type: f.type, required: f.required, options: f.options || [] })),
          inventoryFields: (switchTarget as any).inventoryFields || [],
        }),
      });
      const newKey = String(switchTarget.id);
      setActiveSelection({ type: "custom", key: newKey });
      localStorage.setItem("active_industry_key", newKey);
      showToast(`Switched to ${switchTarget.name}`, "success");
      await fetchTemplates();
      onSwitchApplied?.();
    } catch (err: any) {
      showToast(err.message || "Failed to switch industry template", "error");
    } finally {
      setIsSwitching(false);
      setSwitchTarget(null);
    }
  };

  // ── Menu actions ──
  const handleMenuAction = async (action: string, template: IndustryTemplate) => {
    setOpenMenuId(null);
    switch (action) {
      case "use":
        handleSwitch(template);
        break;

      case "edit-template":
      case "edit-stages":
      case "edit-fields": {
        setWizardName(template.name);
        setWizardIcon(iconOptions.find((o) => o.icon === template.icon) || iconOptions[0]);
        setWizardStages(
          template.stageList.length > 0
            ? template.stageList.map((s, i) => ({ ...s, id: s.id || i + 1, color: resolveColor(s.color) }))
            : DEFAULT_WIZARD_STAGES
        );
        setWizardFields(
          template.fieldList.length > 0
            ? template.fieldList.map((f, i) => ({ ...f, id: f.id || i + 1, options: f.options ? [...f.options] : [] }))
            : DEFAULT_WIZARD_FIELDS
        );
        setEditingTemplateId(template.id);
        setEditingIsActive(template.active);
        setWizardStep(action === "edit-stages" ? 2 : action === "edit-fields" ? 3 : 1);
        setShowWizard(true);
        break;
      }

      case "duplicate":
        try {
          await fetchApi(`/industry-templates/duplicate/${template.id}`, { method: "POST" });
          showToast("Template duplicated successfully", "success");
          await fetchTemplates();
        } catch {
          showToast("Failed to duplicate template", "error");
        }
        break;

      case "delete":
        try {
          await fetchApi(`/industry-templates/delete/${template.id}`, { method: "DELETE" });
          showToast("Template deleted", "success");
          await fetchTemplates();
          onSwitchApplied?.();
        } catch {
          showToast("Failed to delete template", "error");
        }
        break;
    }
  };

  // ── Wizard submit — creates or updates a template ──────
  const handleCreateIndustry = async () => {
    setIsSavingWizard(true);
    try {
      const payload = {
        name: wizardName.trim(),
        icon: wizardIcon.icon,
        iconBg: wizardIcon.bg,
        iconColor: wizardIcon.color,
        stages: wizardStages.map((s) => ({ name: s.name.trim(), color: resolveColor(s.color), required: s.required })),
        fields: wizardFields.map((f) => ({ label: f.label.trim(), type: f.type, required: f.required, options: f.options || [] })),
      };

      let savedTemplateId = editingTemplateId;

      if (editingTemplateId) {
        // Update existing template
        await fetchApi(`/industry-templates/update/${editingTemplateId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Industry template updated", "success");
      } else {
        // Create new template
        const createdRes = await fetchApi<any>("/industry-templates/create", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        savedTemplateId = createdRes?.data?.id || createdRes?.id;
        showToast("Custom industry template created", "success");
      }

      // If the template was active (or if user was editing active template), auto-switch so it applies immediately
      if (editingIsActive && savedTemplateId) {
        await fetchApi("/industry-templates/switch", {
          method: "PATCH",
          body: JSON.stringify({
            type: "custom",
            key: String(savedTemplateId),
            stages: payload.stages,
            fields: payload.fields,
          }),
        });
        const newKey = String(savedTemplateId);
        setActiveSelection({ type: "custom", key: newKey });
        localStorage.setItem("active_industry_key", newKey);
      }

      await fetchTemplates();
      onSwitchApplied?.();
      closeWizard();
    } catch (err: any) {
      showToast(err.message || "Failed to save industry template", "error");
    } finally {
      setIsSavingWizard(false);
    }
  };

  const closeWizard = () => {
    setShowWizard(false);
    setWizardStep(1);
    setWizardName("");
    setEditingTemplateId(null);
    setEditingIsActive(false);
    setWizardIcon(iconOptions[0]);
    setWizardStages(DEFAULT_WIZARD_STAGES);
    setWizardFields(DEFAULT_WIZARD_FIELDS);
  };

  const wizardCanNext = () => {
    if (wizardStep === 1) return wizardName.trim().length > 0;
    if (wizardStep === 2) return wizardStages.length > 0 && wizardStages.every((s) => s.name.trim().length > 0);
    return true;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-center py-12 shadow-sm">
        <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Industry Templates</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Switch presets or customize stages &amp; custom fields for specific industries
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setShowCards(!showCards)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className={`ri-eye-${showCards ? "off-" : ""}line text-sm`} />
            {showCards ? "Hide Cards" : "Show Cards"}
          </button>

          <button
            type="button"
            onClick={() => {
              setEditingTemplateId(null);
              setEditingIsActive(false);
              setWizardName("");
              setWizardStages(DEFAULT_WIZARD_STAGES);
              setWizardFields(DEFAULT_WIZARD_FIELDS);
              setShowWizard(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className="ri-add-line text-sm" /> Add Industry
          </button>
        </div>
      </div>

      {/* Template Grid */}
      {showCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {allTemplates.map((tpl) => (
            <div
              key={tpl.id}
              className={`relative group p-4 rounded-xl border text-left transition-all duration-200 flex flex-col justify-between ${tpl.active
                  ? "border-2 border-indigo-600 bg-indigo-50/40 shadow-sm"
                  : "border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm"
                }`}
            >
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tpl.iconBg}`}>
                      <i className={`${tpl.icon} text-lg ${tpl.iconColor}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">{tpl.name}</h3>
                        {tpl.isCustom && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                            Custom
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500">
                        {tpl.stages} stages &bull; {tpl.fields} custom fields
                      </p>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenMenuId(openMenuId === tpl.id ? null : tpl.id)}
                      className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-more-2-fill text-base" />
                    </button>

                    {openMenuId === tpl.id && (
                      <div
                        ref={menuRef}
                        className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-gray-200 shadow-xl py-1 z-30 animate-scale-in text-left"
                      >
                        {!tpl.active && (
                          <button
                            type="button"
                            onClick={() => handleMenuAction("use", tpl)}
                            className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-indigo-600 font-semibold hover:bg-indigo-50 cursor-pointer"
                          >
                            <i className="ri-check-line text-sm" /> Use Template
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleMenuAction("edit-template", tpl)}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm" /> Edit Template
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMenuAction("edit-stages", tpl)}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-layout-kanban-line text-sm" /> Edit Stages
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMenuAction("edit-fields", tpl)}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-list-check-2 text-sm" /> Edit Fields
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMenuAction("duplicate", tpl)}
                          className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <i className="ri-file-copy-line text-sm" /> Duplicate
                        </button>
                        {tpl.isCustom && (
                          <button
                            type="button"
                            onClick={() => handleMenuAction("delete", tpl)}
                            className="w-full flex items-center gap-2 px-3.5 py-2 text-xs text-red-600 hover:bg-red-50 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" /> Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-2.5 mb-3">
                  <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed font-mono">
                    {tpl.pipeline || "Custom Pipeline"}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                {tpl.active ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 bg-indigo-100/70 px-2.5 py-1 rounded-full">
                    <i className="ri-checkbox-circle-fill text-indigo-600 text-sm" /> Active Template
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSwitch(tpl)}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer flex items-center gap-1"
                  >
                    Switch to this <i className="ri-arrow-right-line text-xs" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleMenuAction("edit-template", tpl)}
                  className="text-xs text-gray-500 hover:text-gray-800 cursor-pointer"
                >
                  Configure
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Switch confirmation modal */}
      {switchTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-sm p-6 text-center animate-scale-in">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-swap-line text-2xl text-indigo-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900">Switch to {switchTarget.name}?</h3>
            <p className="text-xs text-gray-500 mt-2 mb-6">
              This will apply the {switchTarget.name} pipeline stages and lead custom fields across your CRM. Existing leads will retain their current records.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSwitchTarget(null)}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSwitch}
                disabled={isSwitching}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                {isSwitching ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Apply Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Modal (Create & Edit Template) */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
            {/* Wizard Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {editingTemplateId ? `Edit ${wizardName || "Industry Template"}` : "Create Industry Template"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Step {wizardStep} of 3 &bull; {wizardStep === 1 ? "Industry Details" : wizardStep === 2 ? "Pipeline Stages" : "Custom Lead Fields"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeWizard}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Stepper indicator */}
            <div className="flex items-center px-6 py-3 bg-gray-50/70 border-b border-gray-100">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center flex-1 last:flex-none">
                  <button
                    type="button"
                    onClick={() => setWizardStep(step)}
                    className={`flex items-center gap-2 cursor-pointer ${wizardStep === step ? "text-indigo-600 font-bold" : wizardStep > step ? "text-indigo-500 font-medium" : "text-gray-400"
                      }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${wizardStep === step ? "bg-indigo-600 text-white" : wizardStep > step ? "bg-indigo-100 text-indigo-600" : "bg-gray-200 text-gray-600"
                        }`}
                    >
                      {wizardStep > step ? <i className="ri-check-line" /> : step}
                    </span>
                    <span className="text-xs hidden sm:inline">
                      {step === 1 ? "Details" : step === 2 ? "Stages" : "Fields"}
                    </span>
                  </button>
                  {step < 3 && <div className={`flex-1 h-0.5 mx-2 ${wizardStep > step ? "bg-indigo-500" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>

            {/* Wizard Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Step 1: Details */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Industry Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={wizardName}
                      onChange={(e) => setWizardName(e.target.value)}
                      placeholder="e.g. Real Estate, Solar Energy, Insurance"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                      Choose Icon
                    </label>
                    <div className="grid grid-cols-6 gap-2.5">
                      {iconOptions.map((opt, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setWizardIcon(opt)}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer ${wizardIcon.icon === opt.icon
                              ? `${opt.bg} ring-2 ring-indigo-600 scale-105 shadow-sm`
                              : `${opt.bg} opacity-70 hover:opacity-100`
                            }`}
                        >
                          <i className={`${opt.icon} text-lg ${opt.color}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Stages */}
              {wizardStep === 2 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-gray-500">Add and configure pipeline stages for this template:</p>
                    <span className="text-xs font-semibold text-gray-700">{wizardStages.length} Stages</span>
                  </div>

                  {wizardStages.map((stage, idx) => (
                    <div key={idx} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60">
                      <span className="text-xs text-gray-400 w-5 text-center font-semibold">{idx + 1}</span>
                      <div
                        className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: resolveColor(stage.color) }}
                      />
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => {
                          const updated = [...wizardStages];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setWizardStages(updated);
                        }}
                        className="flex-1 bg-white border border-gray-200 rounded-md px-2.5 py-1 text-xs text-gray-900 focus:outline-none focus:border-indigo-400"
                        placeholder="Stage name"
                      />
                      <select
                        value={resolveColor(stage.color)}
                        onChange={(e) => {
                          const updated = [...wizardStages];
                          updated[idx] = { ...updated[idx], color: e.target.value };
                          setWizardStages(updated);
                        }}
                        className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-700 bg-white focus:outline-none"
                      >
                        {stageColors.map((c) => (
                          <option key={c.hex} value={c.hex}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setWizardStages((prev) => prev.filter((_, i) => i !== idx))}
                        disabled={wizardStages.length <= 1}
                        className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const newId = Math.max(0, ...wizardStages.map((s) => Number(s.id) || 0)) + 1;
                      setWizardStages((prev) => [
                        ...prev,
                        { id: newId, name: `Stage ${prev.length + 1}`, color: "#6366f1", required: false },
                      ]);
                    }}
                    className="w-full py-2 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-indigo-600 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                  >
                    + Add Pipeline Stage
                  </button>
                </div>
              )}

              {/* Step 3: Fields */}
              {wizardStep === 3 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-gray-500">Configure custom fields captured on leads:</p>
                    <span className="text-xs font-semibold text-gray-700">{wizardFields.length} Fields</span>
                  </div>

                  {wizardFields.map((field, idx) => {
                    const isSelectType = field.type === "Dropdown" || field.type === "Multi-select";
                    return (
                      <div key={idx} className="p-3 rounded-lg border border-gray-200 bg-gray-50/60 space-y-2.5 transition-all">
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs text-gray-400 w-5 text-center font-semibold">{idx + 1}</span>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => {
                              const updated = [...wizardFields];
                              updated[idx] = { ...updated[idx], label: e.target.value };
                              setWizardFields(updated);
                            }}
                            placeholder="Field label (e.g. Property Type, Budget)"
                            className="flex-1 bg-white border border-gray-200 rounded-md px-2.5 py-1.5 text-xs text-gray-900 focus:outline-none focus:border-indigo-400"
                          />
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const updated = [...wizardFields];
                              const newType = e.target.value;
                              updated[idx] = {
                                ...updated[idx],
                                type: newType,
                                options: (newType === "Dropdown" || newType === "Multi-select")
                                  ? (updated[idx].options && updated[idx].options.length > 0 ? updated[idx].options : ["Option 1", "Option 2"])
                                  : undefined,
                              };
                              setWizardFields(updated);
                            }}
                            className="text-xs border border-gray-200 rounded-md px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:border-indigo-400"
                          >
                            {fieldTypes.map((ft) => (
                              <option key={ft} value={ft}>
                                {ft}
                              </option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => {
                                const updated = [...wizardFields];
                                updated[idx] = { ...updated[idx], required: e.target.checked };
                                setWizardFields(updated);
                              }}
                              className="rounded text-indigo-600 focus:ring-indigo-400"
                            />
                            Req
                          </label>
                          <button
                            type="button"
                            onClick={() => setWizardFields((prev) => prev.filter((_, i) => i !== idx))}
                            className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>

                        {/* Dropdown / Multi-select Options Editor */}
                        {isSelectType && (
                          <div className="pl-7 pt-1 border-t border-gray-200/70">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1">
                                <i className="ri-list-check text-xs" /> Dropdown Options ({field.options?.length || 0}):
                              </span>
                            </div>

                            {/* Option Tags */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {(field.options || []).map((opt, optIdx) => (
                                <span
                                  key={optIdx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-indigo-200 text-indigo-700 text-xs font-medium shadow-2xs"
                                >
                                  {opt}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = [...wizardFields];
                                      const newOpts = (field.options || []).filter((_, i) => i !== optIdx);
                                      updated[idx] = { ...updated[idx], options: newOpts };
                                      setWizardFields(updated);
                                    }}
                                    className="text-indigo-400 hover:text-red-500 cursor-pointer ml-0.5"
                                  >
                                    <i className="ri-close-line text-xs" />
                                  </button>
                                </span>
                              ))}
                              {(!field.options || field.options.length === 0) && (
                                <span className="text-[11px] text-gray-400 italic">No options added yet. Add options below.</span>
                              )}
                            </div>

                            {/* Add Option Input */}
                            <div className="flex items-center gap-2 max-w-sm">
                              <input
                                type="text"
                                placeholder="Type option & press Add or Enter..."
                                id={`new-option-${idx}`}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const input = e.currentTarget;
                                    const val = input.value.trim();
                                    if (val) {
                                      const updated = [...wizardFields];
                                      const currentOpts = field.options || [];
                                      if (!currentOpts.includes(val)) {
                                        updated[idx] = { ...updated[idx], options: [...currentOpts, val] };
                                        setWizardFields(updated);
                                      }
                                      input.value = "";
                                    }
                                  }
                                }}
                                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-indigo-400"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const input = document.getElementById(`new-option-${idx}`) as HTMLInputElement;
                                  if (input && input.value.trim()) {
                                    const val = input.value.trim();
                                    const updated = [...wizardFields];
                                    const currentOpts = field.options || [];
                                    if (!currentOpts.includes(val)) {
                                      updated[idx] = { ...updated[idx], options: [...currentOpts, val] };
                                      setWizardFields(updated);
                                    }
                                    input.value = "";
                                  }
                                }}
                                className="px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer"
                              >
                                Add Option
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => {
                      const newId = Math.max(0, ...wizardFields.map((f) => Number(f.id) || 0)) + 1;
                      setWizardFields((prev) => [
                        ...prev,
                        { id: newId, label: `Custom Field ${prev.length + 1}`, type: "Text Input", required: false },
                      ]);
                    }}
                    className="w-full py-2 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-indigo-600 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                  >
                    + Add Custom Field
                  </button>
                </div>
              )}
            </div>

            {/* Wizard Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => {
                  if (wizardStep === 1) closeWizard();
                  else setWizardStep((s) => s - 1);
                }}
                className="px-4 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                {wizardStep === 1 ? "Cancel" : "Back"}
              </button>

              {wizardStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setWizardStep((s) => s + 1)}
                  disabled={!wizardCanNext()}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold transition-all cursor-pointer"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreateIndustry}
                  disabled={wizardName.trim().length === 0 || isSavingWizard}
                  className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5"
                >
                  {isSavingWizard ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : editingTemplateId ? (
                    "Save & Apply Changes"
                  ) : (
                    "Create Industry Template"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Switch Confirmation Modal */}
      {switchTarget && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => !isSwitching && setSwitchTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-sm overflow-hidden animate-scale-in">
              <div className="p-5 text-center">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${switchTarget.iconBg}`}>
                  <i className={`${switchTarget.icon} text-2xl ${switchTarget.iconColor}`} />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Switch to {switchTarget.name}?
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  This will update your live Pipeline Stages, Custom Fields, and Inventory Fields to match {switchTarget.name}.
                </p>
                <div className="mt-4 bg-gray-50 rounded-lg p-3 text-left text-xs text-gray-600 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 text-sm" />
                    <span>{switchTarget.stages} Pipeline Stages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 text-sm" />
                    <span>{switchTarget.fields} Lead Custom Fields</span>
                  </div>
                </div>
              </div>
              <div className="flex border-t border-gray-100 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => setSwitchTarget(null)}
                  disabled={isSwitching}
                  className="flex-1 px-4 py-3 text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmSwitch}
                  disabled={isSwitching}
                  className="flex-1 px-4 py-3 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors border-l border-indigo-700 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isSwitching ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Switching...
                    </>
                  ) : (
                    "Confirm Switch"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}